import { Tags } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../filters/FilterSection';

interface ClassificationSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  commodityGroups: Array<{ id: number; name: string }>;
  commodityTypes: Array<{ id: number; name: string; commodity_group?: number }>;
  /** CR-4800: supplier/order type are ERP concepts; compliance swaps in its own. */
  showPurchasingFilters: boolean;
  isComplianceMode: boolean;
  /** CR-4802: tenant-declared visibility for Supplier Type and the Commodity pair. */
  showSupplierType?: boolean;
  showCommodity?: boolean;
}

/**
 * CR-6401: CLASSIFICATION group — how an order or supplier is categorised.
 * Commodity Group and Commodity Type were previously in separate rows despite
 * one cascading into the other (CR-6355); they now sit side by side.
 *
 * CR-4802: Commodity Group / Commodity Type are hidden together when the
 * tenant hasn't configured any commodity groups in Admin Setup — mirrors the
 * existing `divisions.length > 0` gate on the Division filter (CR-4349).
 * `commodityGroups` here is the full tenant-scoped list fetched by
 * useDashboardData, not narrowed by the current selection, so it's a valid
 * "is this configured at all" signal.
 */
export function ClassificationSection({
  filters,
  setFilters,
  commodityGroups,
  commodityTypes,
  showPurchasingFilters,
  isComplianceMode,
  showSupplierType = true,
  showCommodity = true,
}: ClassificationSectionProps) {
  // CR-4802: when the tenant hides every control in this group, drop the
  // section rather than render an empty "Classification" heading.
  const hasPurchasingControls = showPurchasingFilters && (showSupplierType || (showCommodity && commodityGroups.length > 0));
  if (!hasPurchasingControls && !isComplianceMode) return null;
  return (
    <FilterSection title="Classification" icon={Tags}>
      <FilterGrid>
        {showPurchasingFilters && showSupplierType && (
          <FilterField label="Supplier type">
            <select
              value={filters.supplierType}
              onChange={(e) => setFilters({ ...filters, supplierType: e.target.value })}
              className={filterControlClass}
            >
              <option value="">All suppliers</option>
              <option value="internal">Internal Suppliers</option>
              <option value="external">External Suppliers</option>
            </select>
          </FilterField>
        )}

        {/* CR-7273: the Order type control is gone. It offered blanket and
            contract, which PurchaseOrder.order_type cannot hold, and its
            value was never added to the request — so it reported a filter
            applied and changed nothing. The column has no writer on any
            tenant (every row is the 'scheduled' default), so there is nothing
            for it to narrow even once it is wired up. Removed here to match
            Deliveries and Supplier Performance; the dashboard's separate
            order-type *mode*, which is sent as order_type, is untouched. */}

        {showCommodity && commodityGroups.length > 0 && (
          <FilterField label="Commodity group">
            <select
              value={filters.commodityGroup}
              onChange={(e) => {
                // CR-6355: reset a now-invalid type so the pair never describes
                // an impossible combination (which collapses the data to zero).
                const val = e.target.value;
                const next = { ...filters, commodityGroup: val };
                if (val !== filters.commodityGroup) next.commodityType = '';
                setFilters(next);
              }}
              className={filterControlClass}
            >
              <option value="">All commodity groups</option>
              {commodityGroups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </FilterField>
        )}

        {showCommodity && commodityGroups.length > 0 && (
          <FilterField label="Commodity type">
            <select
              value={filters.commodityType}
              onChange={(e) => setFilters({ ...filters, commodityType: e.target.value })}
              className={filterControlClass}
            >
              <option value="">All commodity types</option>
              {commodityTypes
                // CR-6355: restricted to the selected group's members.
                .filter((type) => !filters.commodityGroup || String(type.commodity_group) === filters.commodityGroup)
                .map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
            </select>
          </FilterField>
        )}

        {/* CR-4800: compliance-only filters replace the ERP ones above. */}
        {isComplianceMode && (
          <FilterField label="Approval stage">
            <select
              value={filters.approvalStage}
              onChange={(e) => setFilters({ ...filters, approvalStage: e.target.value })}
              className={filterControlClass}
            >
              <option value="">All Stages</option>
              <option value="Not Invited">Not Invited</option>
              <option value="Invited">Invited</option>
              <option value="In Progress">In Progress</option>
              <option value="Submitted / Under Review">Submitted / Under Review</option>
              <option value="More Info Required">More Info Required</option>
              <option value="Approved">Approved</option>
              <option value="Declined">Declined</option>
              <option value="Contract Awarded">Contract Awarded</option>
            </select>
          </FilterField>
        )}

        {isComplianceMode && (
          <FilterField label="Risk category">
            <select
              value={filters.riskCategory}
              onChange={(e) => setFilters({ ...filters, riskCategory: e.target.value })}
              className={filterControlClass}
            >
              <option value="">All Risk Levels</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </FilterField>
        )}
      </FilterGrid>
    </FilterSection>
  );
}
